import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	Lightbulb,
	Plus,
	Trophy,
	ChevronDown,
	Star,
} from "lucide-react";
import type { User } from "@/lib/types";
import type { ViewMode } from "@/lib/types";

interface HeaderProps {
	currentUser: User;
	users: User[];
	onNavigate: (view: ViewMode, data?: string) => void;
	onSwitchUser: (userId: string) => void;
}

export function Header({
	currentUser,
	users,
	onNavigate,
	onSwitchUser,
}: HeaderProps) {
	return (
		<header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
			<div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
				<button
					onClick={() => onNavigate("feed")}
					className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
				>
					<div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
						<Lightbulb className="w-5 h-5 text-white" />
					</div>
					<div className="flex flex-col">
						<span className="text-base font-bold leading-tight tracking-tight">
							Startup Idea Arena
						</span>
						<span className="text-[10px] text-muted-foreground leading-tight">
							Submit. Vote. Fork. Build.
						</span>
					</div>
				</button>

				<nav className="hidden sm:flex items-center gap-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onNavigate("feed")}
						className="text-sm"
					>
						Feed
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => onNavigate("rankings")}
						className="text-sm"
					>
						<Trophy className="w-4 h-4 mr-1" />
						Rankings
					</Button>
				</nav>

				<div className="flex items-center gap-3">
					<Button
						size="sm"
						onClick={() => onNavigate("submit")}
						className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-sm"
					>
						<Plus className="w-4 h-4 mr-1" />
						Submit Idea
					</Button>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="gap-2 pl-2 pr-1.5"
							>
								<Avatar className="w-7 h-7">
									<AvatarFallback className="text-xs bg-primary text-primary-foreground">
										{currentUser.avatar}
									</AvatarFallback>
								</Avatar>
								<div className="hidden sm:flex items-center gap-1.5">
									<span className="text-sm font-medium">
										{currentUser.name}
									</span>
									<Badge
										variant="secondary"
										className="text-[10px] px-1.5 py-0 h-5"
									>
										<Star className="w-3 h-3 mr-0.5" />
										{currentUser.reputation}
									</Badge>
								</div>
								<ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<div className="px-2 py-1.5">
								<p className="text-xs text-muted-foreground">
									Switch user (demo)
								</p>
							</div>
							<DropdownMenuSeparator />
							{users.map((user) => (
								<DropdownMenuItem
									key={user.id}
									onClick={() => onSwitchUser(user.id)}
									className="gap-2"
								>
									<Avatar className="w-6 h-6">
										<AvatarFallback className="text-[10px] bg-muted">
											{user.avatar}
										</AvatarFallback>
									</Avatar>
									<span className="flex-1 text-sm">{user.name}</span>
									<Badge
										variant="outline"
										className="text-[10px] px-1.5 py-0 h-5"
									>
										<Star className="w-3 h-3 mr-0.5" />
										{user.reputation}
									</Badge>
									{user.id === currentUser.id && (
										<span className="text-[10px] text-green-600 font-medium">
											Active
										</span>
									)}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
		</header>
	);
}
